import { setModalOpen } from "@/store/slices/modalSlice";
import { store } from "@/store/store";

// This interface is incomplete. Check here for a list of all fields https://developer.helpscout.com/beacon-2/web/javascript-api/#beaconconfig-formobject
interface Config {
  display?: {
    zIndex?: number;
    horizontalMobileOffset?: number;
    verticalMobileOffset?: number;
  };
}

interface UserData {
  name: string;
  email: string;
  jobTitle: string | undefined;
  "organisation-id": number;
  "organisation-name": string;
  signature: string;
}
interface MessageConfig {
  force?: boolean;
  delay?: number;
}
type Event =
  | "open"
  | "close"
  | "ready"
  | "article-viewed"
  | "chat-started"
  | "email-sent"
  | "message-clicked"
  | "message-closed"
  | "message-triggered"
  | "search";
type RawBeaconAction =
  | "init"
  | "open"
  | "close"
  | "toggle"
  | "identify"
  | "on"
  | "off"
  | "config"
  | "article"
  | "ask-question"
  | "show-message"
  | "destroy";
type RawBeaconFunc = (
  type: RawBeaconAction,
  arg?: UserData | Event | string | Config | MESSAGE_ID,
  arg2?: (() => void) | MessageConfig
) => void;

type BeaconAction = Exclude<RawBeaconAction, "init" | "destroy">;
type BeaconFunc = (type: BeaconAction, arg?: UserData | Event | string | Config | MESSAGE_ID, arg2?: (() => void) | MessageConfig) => void;

type MESSAGE_ID = "434546e4-b714-4363-a10f-896c1fd0a454";

let hasBeenInitialised = false;
let scriptLoaded = false;

const BEACON_SESSION_CONSENT_KEY = "beacon-session-consent";

const getBeaconSessionConsent = (): boolean => {
  return sessionStorage.getItem(BEACON_SESSION_CONSENT_KEY) === "true";
};

const setBeaconSessionConsent = (): void => {
  sessionStorage.setItem(BEACON_SESSION_CONSENT_KEY, "true");
};

const hasBeacon = (window: unknown): window is { Beacon: RawBeaconFunc } => {
  return typeof window === "object" && window != null && "Beacon" in window && typeof window.Beacon === "function";
};

const BEACON_MODAL_NAME = "beacon_modal";
const BeaconDefaultConfig: Config = {
  display: {
    horizontalMobileOffset: 20,
    verticalMobileOffset: 20,
    zIndex: 9999
  }
};

const PRODUCT_ISSUE_FEEDBACK_MESSAGE_ID: MESSAGE_ID = "434546e4-b714-4363-a10f-896c1fd0a454";

const loadBeaconScript = () => {
  if (scriptLoaded) {
    return;
  }
  scriptLoaded = true;

  const w = window as unknown as { Beacon: RawBeaconFunc & { readyQueue: unknown[] } };
  w.Beacon = function (method, options, data) {
    w.Beacon.readyQueue.push({ method, options, data });
  } as RawBeaconFunc & { readyQueue: unknown[] };
  w.Beacon.readyQueue = [];

  const first = document.getElementsByTagName("script")[0];
  const script = document.createElement("script");
  script.type = "text/javascript";
  script.async = true;
  script.src = "https://beacon-v2.helpscout.net";
  first.parentNode?.insertBefore(script, first);
};

const rawBeacon: RawBeaconFunc = (type, arg, arg2) => {
  if (!hasBeacon(window)) {
    return;
  }
  window.Beacon(type, arg, arg2);
};

const initBeacon = () => {
  if (hasBeenInitialised) {
    return;
  }
  loadBeaconScript();
  hasBeenInitialised = true;

  rawBeacon("on", "open", () => store.dispatch(setModalOpen({ name: BEACON_MODAL_NAME, value: true })));
  rawBeacon("on", "close", () => store.dispatch(setModalOpen({ name: BEACON_MODAL_NAME, value: false })));
  rawBeacon("config", BeaconDefaultConfig);
  rawBeacon("init", import.meta.env.VITE_BEACON_PUBLIC_SECRET);
};

const destroyBeacon = () => {
  if (!hasBeenInitialised) {
    return;
  }
  rawBeacon("destroy");
  hasBeenInitialised = false;
  sessionStorage.removeItem(BEACON_SESSION_CONSENT_KEY);
};

const Beacon: BeaconFunc = (type, arg, arg2) => {
  if (!hasBeenInitialised) {
    setBeaconSessionConsent();
    initBeacon();
  }
  rawBeacon(type, arg, arg2);
};

export {
  Beacon,
  BEACON_MODAL_NAME,
  BeaconDefaultConfig,
  destroyBeacon,
  getBeaconSessionConsent,
  initBeacon,
  PRODUCT_ISSUE_FEEDBACK_MESSAGE_ID
};
export type { MESSAGE_ID };
